import { CheckCircleIcon } from '@heroicons/react/24/solid/index.js';
import GoogleAnalyticsIcon from '../icons/GoogleAnalyticsIcon.jsx';
import GoogleTagManagerIcon from '../icons/GoogleTagManagerIcon.jsx';
import MetaPixelIcon from '../icons/MetaPixelIcon.jsx';
import ClarityIcon from '../icons/ClarityIcon.jsx';
import Modal from '../Modal';
import { useEffect, useState } from 'react';
import {
    setOtherIntegrationsQuery,
    useOtherIntegrationsQuery,
} from '../../lib/Queries';
import useBusinessPostApi from '../../lib/useBusinessPostApi.js';
import TextInput from '../TextInput';
import IntegrationSettingsBlock from './IntegrationSettingsBlock.jsx';
import ZoomIntegrationBlock from './ZoomIntegrationBlock.jsx';
import ZapierLogo from '~/components/icons/ZapierLogo.jsx';
import GoogleIcon from "~/components/icons/GoogleIcon";
import {getBusinessUrl} from "~/lib/useBusinessNavigate";
import {Link} from "react-router-dom";
import { ChartBarIcon, CpuChipIcon } from '@heroicons/react/24/outline';

const OtherIntegrations = ({}) => {
    const { data, isLoading } = useOtherIntegrationsQuery();

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-4xl">
            {!!data?.googleanalytics?.id && (
                <IntegrationSettingsBlock
                    icon={<GoogleAnalyticsIcon />}
                    title="Google Analytics"
                    description="Discover where your visitors come from and what they do on your site."
                    isConnected={!!data?.googleanalytics?.id}
                    integrationKey="googleanalytics"
                    settings={data?.googleanalytics}
                    getSettingsContent={({ settings, setSettings }) => (
                        <div>
                            <TextInput
                                value={settings?.id || ''}
                                onChange={value =>
                                    setSettings({ ...settings, id: value })
                                }
                                placeholder="G-XXXXXXXXXX"
                                label="Tag ID"
                                autoFocus={true}
                            />
                        </div>
                    )}
                />
            )}

            <IntegrationSettingsBlock
                icon={<GoogleTagManagerIcon />}
                title="Google Tag Manager"
                description="Use Google Tag Manager to update tags and code on your website."
                isConnected={!!data?.googletagmanager?.id}
                integrationKey="googletagmanager"
                settings={data?.googletagmanager}
                getSettingsContent={({ settings, setSettings }) => (
                    <div>
                        <TextInput
                            value={settings?.id || ''}
                            onChange={value =>
                                setSettings({ ...settings, id: value })
                            }
                            placeholder="GTM-XXXXXXXX"
                            label="Tag ID"
                            autoFocus={true}
                        />
                    </div>
                )}
            />

            {!!data?.metapixel?.id && (
                <IntegrationSettingsBlock
                    icon={<MetaPixelIcon />}
                    title="Meta Pixel"
                    description="Track conversions and measure the success of Facebook campaigns."
                    isConnected={!!data?.metapixel?.id}
                    integrationKey="metapixel"
                    settings={data?.metapixel}
                    getSettingsContent={({ settings, setSettings }) => (
                        <div>
                            <TextInput
                                value={settings?.id || ''}
                                onChange={value =>
                                    setSettings({ ...settings, id: value })
                                }
                                placeholder="1122334455667788"
                                label="Pixel ID"
                                autoFocus={true}
                            />
                        </div>
                    )}
                />
            )}

            <IntegrationSettingsBlock
                icon={<ChartBarIcon className="text-primary-700" />}
                title="Conversion Tracking (Google, Meta, etc.)"
                description="Track conversions and measure the success of your marketing campaigns."
                isConnected={false}
                integrationKey="conversiontracking"
                settings={null}
                hideSave={true}
                getSettingsContent={({ settings, setSettings }) => (
                    <div>
                        Learn how to set up conversion tracking for Google Ads, Meta Ads, and other platforms using Google Tag Manager.
                        <a
                            className="block mt-2 text-blue-600 hover:text-blue-800"
                            href="https://support.breely.com/en/articles/11932009-conversion-tracking-with-google-tag-manager"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            View Conversion Tracking Guide →
                        </a>

                        <p className="mt-4 pt-4 border-t">
                            Or, redirect to a Thank You page on your own website after the form is submitted. This can be configured by editing a form, clicking the 'Submission Confirmation' block on the thank you section, and setting the 'Redirect URL' to your thank you page.
                        </p>
                    </div>
                )}
            />

            <IntegrationSettingsBlock
                icon={<ClarityIcon />}
                title="Microsoft Clarity"
                description="Understand how users interact with your website through heatmaps and session recordings."
                isConnected={!!data?.clarity?.id}
                integrationKey="clarity"
                settings={data?.clarity}
                getSettingsContent={({ settings, setSettings }) => (
                    <div>
                        <TextInput
                            value={settings?.id || ''}
                            onChange={value =>
                                setSettings({ ...settings, id: value })
                            }
                            placeholder="abcd1234"
                            label="Project ID"
                            autoFocus={true}
                        />
                    </div>
                )}
            />

            <ZoomIntegrationBlock />

            <IntegrationSettingsBlock
                icon={<GoogleIcon />}
                title="Google Meet"
                description="Generate Google Meet meetings for events."
                isConnected={false}
                integrationKey="googlemeet"
                settings={null}
                hideSave={true}
                getSettingsContent={({ settings, setSettings }) => (
                    <div>
                        To create Google Meet links automatically for bookings:
                        <ol className="text-base mt-2">
                            <li>Connect your Google Calendar under Integrations &gt; Calendar Syncing</li>
                            <li>In <Link to={getBusinessUrl(`/scheduling/event-types`)}>Event Types</Link>, edit a type, and set the Location to Google Meet</li>
                        </ol>
                    </div>
                )}
            />

            <IntegrationSettingsBlock
                icon={<ZapierLogo />}
                title="Zapier"
                description="Integrate with thousands of other apps via Zapier."
                isConnected={false}
                integrationKey="zapier"
                settings={null}
                hideSave={true}
                getSettingsContent={({ settings, setSettings }) => (
                    <div>
                        Zapier is available only through the invite link below, at this time
                        it isn't published in Zapier's marketplace.
                        <a className="block mt-2" href="https://zapier.com/developer/public-invite/209989/20e941604ddf1de733d759339c85e5d9/" target="_blank">
                            Use Breely on Zapier →
                        </a>
                    </div>
                )}
            />

            {/*<IntegrationSettingsBlock*/}
            {/*    icon={<CpuChipIcon className="text-primary-700" />}*/}
            {/*    title="MCP (Model Context Protocol)"*/}
            {/*    description="Connect AI platforms to access your Breely data through the Model Context Protocol."*/}
            {/*    isConnected={false}*/}
            {/*    integrationKey="mcp"*/}
            {/*    settings={null}*/}
            {/*    hideSave={true}*/}
            {/*    getSettingsContent={({ settings, setSettings }) => (*/}
            {/*        <div>*/}
            {/*            MCP integration allows AI platforms to securely access your Breely data and perform actions on your behalf.*/}
            {/*            <a*/}
            {/*                className="block mt-2 text-blue-600 hover:text-blue-800"*/}
            {/*                href="https://support.breely.com"*/}
            {/*                target="_blank"*/}
            {/*                rel="noopener noreferrer"*/}
            {/*            >*/}
            {/*                Submit a support request for MCP access →*/}
            {/*            </a>*/}
            {/*        </div>*/}
            {/*    )}*/}
            {/*/>*/}
        </div>
    );
};

export default OtherIntegrations;
